import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuthState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, Eye, EyeOff, Gift, RefreshCw, Mail, Shield } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook } from "react-icons/fa";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { validateSAIDNumber } from "@/lib/idValidation";
import { OTPVerification } from "@/components/OTPVerification";

export default function AuthPage() {
  const [, navigate] = useLocation();
  const {
    signIn,
    signUp,
    signInWithMagicLink,
    signInWithGoogle,
    signInWithFacebook,
    loading,
    resetPassword,
    updatePassword,
  } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("signin");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const signupToggleTimer = useRef<NodeJS.Timeout | null>(null);
  const loginToggleTimer = useRef<NodeJS.Timeout | null>(null);
  const [redirect, setRedirect] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameGenerated, setUsernameGenerated] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [idNumberError, setIdNumberError] = useState("");
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [signupPhone, setSignupPhone] = useState("");
  const [pendingRedirect, setPendingRedirect] = useState("");
  const autoHideDuration = 5000;

  const clearTimer = (ref: { current: NodeJS.Timeout | null }) => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const handleSignupToggle = () => {
    setShowSignupPassword((prev) => {
      const next = !prev;
      clearTimer(signupToggleTimer);
      if (next) {
        signupToggleTimer.current = setTimeout(() => setShowSignupPassword(false), autoHideDuration);
      }
      return next;
    });
  };

  const handleLoginToggle = () => {
    setShowLoginPassword((prev) => {
      const next = !prev;
      clearTimer(loginToggleTimer);
      if (next) {
        loginToggleTimer.current = setTimeout(() => setShowLoginPassword(false), autoHideDuration);
      }
      return next;
    });
  };

  useEffect(() => {
    return () => {
      clearTimer(signupToggleTimer);
      clearTimer(loginToggleTimer);
    };
  }, []);

  // Check for reset password mode and auth type in URL
  useEffect(() => {
    // Check for deactivated account error message on page load
    const deactivatedError = sessionStorage.getItem('authErrorMessage');
    if (deactivatedError) {
      setError(deactivatedError);
      toast({
        title: "Account Deactivated",
        description: deactivatedError,
        variant: "destructive",
        duration: 10000,
      });
      sessionStorage.removeItem('authErrorMessage');
      setIsLoading(false);
    }

    // Listen for deactivation events from the auth system
    const handleDeactivation = (event: CustomEvent) => {
      const errorMessage = event.detail.message;
      console.log('[AUTH PAGE] Deactivation event received:', errorMessage);
      setError(errorMessage);
      setIsLoading(false);
      toast({
        title: "Account Deactivated",
        description: errorMessage,
        variant: "destructive",
        duration: 10000,
      });
    };

    window.addEventListener('authDeactivated', handleDeactivation as EventListener);

    const urlParams = new URLSearchParams(window.location.search);
    const authType = urlParams.get("type");
    const mode = urlParams.get("mode");

    console.log("[AUTH PAGE] URL params detected:", {
      authType,
      mode,
      searchParams: urlParams.toString(),
    });

    // Handle password reset mode
    if (mode === "reset-password" || authType === "recovery") {
      setShowUpdatePassword(true);
      setActiveTab("signin");
    }

    // Handle different auth types from email redirects
    if (authType === "signup") {
      console.log(
        "[AUTH PAGE] Email verification detected, showing success message",
      );
      setVerificationMessage(
        "Your email has been verified! You can now sign in with your credentials.",
      );
      setActiveTab("signin");
    } else if (authType === "magiclink") {
      console.log("[AUTH PAGE] Magic link authentication detected");
      setVerificationMessage("Processing magic link authentication...");
      setActiveTab("signin");
    } else if (authType === "oauth") {
      console.log(
        "[AUTH PAGE] OAuth authentication detected, checking session...",
      );
      setVerificationMessage("Completing Facebook/Google authentication...");
      setActiveTab("signin");
    }

    // Check for redirect and referral parameters
    let redirectUrl = urlParams.get("redirect");
    let ref = urlParams.get("ref");
    let intent = urlParams.get("intent");
    let challengeId = urlParams.get("challengeId");
    
    // Facebook ad tracking parameters
    const adId = urlParams.get("ad_id");
    const campaignId = urlParams.get("campaign_id");
    let compId = urlParams.get("comp");

    // CRITICAL: Restore referral parameters after OAuth callback
    // If we're returning from OAuth, restore parameters from sessionStorage as fallback
    if (authType === "oauth") {
      console.log("[AUTH PAGE] Restoring referral parameters after OAuth callback...");
      
      const paramsToRestore = ['ref', 'challengeId', 'social', 'comp', 'intent', 'redirect', 'autoJoinChallenge', 'mobile'];
      const restoredParams: { [key: string]: string } = {};
      
      paramsToRestore.forEach(param => {
        const urlValue = urlParams.get(param);
        const facebookSessionValue = sessionStorage.getItem(`facebook_oauth_${param}`);
        const googleSessionValue = sessionStorage.getItem(`google_oauth_${param}`);
        
        // URL parameters take priority over sessionStorage
        if (urlValue) {
          restoredParams[param] = urlValue;
        } else if (facebookSessionValue) {
          restoredParams[param] = facebookSessionValue;
        } else if (googleSessionValue) {
          restoredParams[param] = googleSessionValue;
        }
      });

      // Apply restored parameters
      if (restoredParams.ref) ref = restoredParams.ref;
      if (restoredParams.challengeId) challengeId = restoredParams.challengeId;
      if (restoredParams.intent) intent = restoredParams.intent;
      if (restoredParams.comp) compId = restoredParams.comp;
      if (restoredParams.redirect) redirectUrl = restoredParams.redirect;

      console.log("[AUTH PAGE] Restored referral parameters:", restoredParams);

      // Clean up sessionStorage after restoration (both Facebook and Google)
      paramsToRestore.forEach(param => {
        sessionStorage.removeItem(`facebook_oauth_${param}`);
        sessionStorage.removeItem(`google_oauth_${param}`);
      });
    }

    // Check for tab parameter or intent
    const tab = urlParams.get("tab");
    const comp = urlParams.get("comp");
    const isCompetitionEntry =
      redirectUrl && redirectUrl.includes("/competition/");
    
    // Check if the pathname is /signup or /m/signup
    const pathname = window.location.pathname;
    const isSignupRoute = pathname === "/signup" || pathname === "/m/signup";

    if (tab === "signup" || isSignupRoute) {
      setActiveTab("signup");
    } else if (intent === "join") {
      // Default to signup tab for challenge entry flows
      setActiveTab("signup");
    } else if (comp || isCompetitionEntry) {
      // Default to signup tab for competition entry flows
      setActiveTab("signup");
    }

    // If intent is to join a challenge, redirect directly to challenge page
    // (backend already auto-joins users who sign up from challenge pages)
    if (intent === "join" && challengeId) {
      setRedirect(`/challenges/${challengeId}`);
    } else if (redirectUrl) {
      setRedirect(redirectUrl);
    }

    // Check for session-based referral storage (server-side approach)
    const sessionId = sessionStorage.getItem("referral_session_id");
    const storedReferralId = sessionStorage.getItem("pending_referral_id");

    // Set referral code from URL parameters (this takes priority)
    if (ref) {
      setReferralCode(ref);
      console.log("[AUTH] Using referral code from URL:", ref);
    }

    // If there's a stored referral session, check for any redirect context
    if (sessionId || storedReferralId) {
      console.log(
        "[AUTH] Found referral session storage - will process after authentication",
      );

      // If we have session storage from a referral, we'll let the challenge page handle the redirect
      // after authentication since the actual referral data is stored server-side
    }

    // Check for verification message
    const message = localStorage.getItem("authVerificationMessage");
    if (message) {
      setVerificationMessage(message);
      localStorage.removeItem("authVerificationMessage");
      setActiveTab("signin");
    }

    // Check for session in URL hash or query params (Supabase auth response)
    const hash = window.location.hash;
    const searchParams = window.location.search;

    if (
      hash &&
      (hash.includes("access_token") || hash.includes("refresh_token"))
    ) {
      console.log(
        "[AUTH PAGE] Session tokens detected in URL hash, Supabase should handle authentication",
      );
      setVerificationMessage("Completing authentication...");
    } else if (
      searchParams &&
      (searchParams.includes("code=") || searchParams.includes("state="))
    ) {
      console.log(
        "[AUTH PAGE] OAuth code/state detected in URL, processing authentication...",
      );
      setVerificationMessage("Completing Google authentication...");
    }

    // Cleanup event listener
    return () => {
      window.removeEventListener('authDeactivated', handleDeactivation as EventListener);
    };
  }, []);

  // Generate a unique username when tab switches to signup
  useEffect(() => {
    if (activeTab === "signup" && !usernameGenerated && !username) {
      generateUsername();
    }
  }, [activeTab]);

  // Generate username function
  const generateUsername = async () => {
    try {
      const response = await fetch("/api/generate-username");
      const data = await response.json();
      if (data.username) {
        setUsername(data.username);
        setUsernameGenerated(true);
        setUsernameError("");
      }
    } catch (error) {
      console.error("Error generating username:", error);
      toast({
        title: "Error",
        description: "Failed to generate username. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Debounced username availability check
  const checkUsernameAvailability = useCallback(
    async (usernameToCheck: string) => {
      if (!usernameToCheck || usernameToCheck.length < 3) {
        setUsernameError(
          usernameToCheck.length > 0
            ? "Username must be at least 3 characters"
            : "",
        );
        return;
      }

      setUsernameChecking(true);
      setUsernameError("");

      try {
        const response = await fetch("/api/check-username", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username: usernameToCheck }),
        });

        const data = await response.json();

        if (!data.available) {
          setUsernameError(data.error || "Username is already taken");
        } else {
          setUsernameError("");
        }
      } catch (error) {
        console.error("Error checking username:", error);
        setUsernameError("Error checking availability");
      } finally {
        setUsernameChecking(false);
      }
    },
    [],
  );

  // Debounced username check
  useEffect(() => {
    if (!username || usernameGenerated) {
      // Skip validation for auto-generated usernames
      if (usernameGenerated) {
        setUsernameGenerated(false);
      }
      return;
    }

    const timeoutId = setTimeout(() => {
      checkUsernameAvailability(username);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [username, checkUsernameAvailability, usernameGenerated]);

  const handleUsernameChange = (value: string) => {
    // Convert to lowercase and remove invalid characters
    const cleanedUsername = value.toLowerCase().replace(/[^a-z0-9]/g, "");
    setUsername(cleanedUsername);
    setUsernameGenerated(false);
  };

  // Email validation function
  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError("");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }

    setEmailError("");
    return true;
  };

  // Phone validation function
  const validatePhone = (phone: string) => {
    if (!phone) {
      setPhoneError("");
      return false;
    }

    // Remove all spaces, dashes, keep only digits and +
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "").replace(/[^\d+]/g, "");

    // Count only digits (not +)
    const digitsOnly = cleanPhone.replace(/\+/g, "");

    // Check max length: 12 digits
    if (digitsOnly.length > 12) {
      setPhoneError("Phone number cannot exceed 12 digits");
      return false;
    }

    // Phone numbers must have at least 10 digits
    if (digitsOnly.length < 10) {
      setPhoneError("Phone number must be at least 10 digits");
      return false;
    }

    // SA mobile prefixes (after removing country code)
    const validMobilePrefixes = ['060', '061', '062', '063', '064', '065', '066', '067', '068', '069', 
                                  '070', '071', '072', '073', '074', '075', '076', '077', '078', '079',
                                  '080', '081', '082', '083', '084', '085', '086', '087', '088', '089'];

    // Local format: 0XXXXXXXXX (10 digits starting with 06, 07, or 08)
    if (cleanPhone.match(/^0[6-8]\d{8}$/)) {
      const prefix = cleanPhone.substring(0, 3);
      if (validMobilePrefixes.includes(prefix)) {
        setPhoneError("");
        return true;
      }
      setPhoneError("Invalid SA mobile prefix. Must start with 06, 07, or 08");
      return false;
    }

    // International format: +27XXXXXXXXX or 27XXXXXXXXX (11-12 digits)
    if (cleanPhone.match(/^(\+27|27)[6-8]\d{8}$/)) {
      const localPart = cleanPhone.replace(/^(\+27|27)/, '0');
      const prefix = localPart.substring(0, 3);
      if (validMobilePrefixes.includes(prefix)) {
        setPhoneError("");
        return true;
      }
      setPhoneError("Invalid SA mobile prefix. Must start with 06, 07, or 08");
      return false;
    }

    setPhoneError("Please enter a valid SA mobile number (e.g., 0712345678 or +27712345678)");
    return false;
  };

  // Check phone number availability function
  const checkPhoneAvailability = async (phone: string) => {
    if (!phone || !validatePhone(phone)) {
      return;
    }

    try {
      const response = await fetch('/api/check-phone-number', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });

      const data = await response.json();

      if (!data.available) {
        const errorMsg = data.message || "This phone number is already registered with another account.";
        setPhoneError(errorMsg);
        return false;
      }

      setPhoneError("");
      return true;
    } catch (error) {
      console.error('Error checking phone availability:', error);
      setPhoneError("Unable to verify phone number. Please try again.");
      return false;
    }
  };

  // Password validation function
  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordError("");
      return false;
    }

    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setPasswordError(
        "Password must contain uppercase, lowercase, and numbers",
      );
      return false;
    }

    setPasswordError("");
    return true;
  };

  // ID Number validation function
  // COMMENTED OUT - ID VERIFICATION REMOVED
  // const validateIdNumber = (idNumber: string) => {
  //   if (!idNumber) {
  //     setIdNumberError("");
  //     return false;
  //   }

  //   const validation = validateSAIDNumber(idNumber);

  //   if (!validation.isValid) {
  //     setIdNumberError(validation.error || "Invalid ID number");
  //     return false;
  //   }

  //   setIdNumberError("");
  //   return true;
  // };

  // Check if form is valid
  const isFormValid = () => {
    return (
      !emailError &&
      !phoneError &&
      !passwordError &&
      !idNumberError &&
      !usernameError &&
      username &&
      !usernameChecking
    );
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    const { error } = await signIn(email, password);

    if (error) {
      // Check if it's an email confirmation error
      if (error.message.includes("Email not confirmed")) {
        setError(
          "Please verify your email before signing in. Check your inbox (and spam folder) for the verification email.",
        );
        toast({
          title: "Email Verification Required",
          description:
            "Please check your email to verify your account. The verification email may take 2-5 minutes to arrive.",
          variant: "destructive",
          duration: 10000,
        });
      } else {
        setError(error.message);
        toast({
          title: "Sign in failed",
          description: error.message,
          variant: "destructive",
        });
      }
      setIsLoading(false);
    } else {
      console.log(
        "[AUTH] Sign in successful, waiting for auth state and account verification...",
      );

      // Don't show success toast yet - wait for account status check
      // The fetchUserData function in useAuthState will verify the account is active
      // and handle the redirect or show deactivation error

      // Set a timeout to handle loading state in case auth state doesn't update quickly
      setTimeout(() => {
        // Check if there's a deactivation error message
        const deactivatedError = sessionStorage.getItem('authErrorMessage');
        if (deactivatedError) {
          // Error will be shown by the useEffect, just stop loading
          setIsLoading(false);
        } else {
          // If no error after 3 seconds, assume success
          setIsLoading(false);
        }
      }, 3000);
    }
  };

  const handleMagicLinkSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsMagicLinkLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("magic-email") as string;

    const { error } = await signInWithMagicLink(email);

    if (error) {
      setError(error.message);
      toast({
        title: "Magic link failed",
        description: error.message,
        variant: "destructive",
      });
      setIsMagicLinkLoading(false);
    } else {
      setMagicLinkSent(true);
      toast({
        title: "Magic link sent!",
        description: "Please check your email for the sign-in link.",
        variant: "default",
      });
      setIsMagicLinkLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError("");

    // CRITICAL: Preserve referral parameters before OAuth redirect (same as Facebook)
    // Store all referral-related parameters in sessionStorage
    const urlParams = new URLSearchParams(window.location.search);
    const referralParams: { [key: string]: string } = {};
    
    // Capture all potential referral parameters
    const paramsToPreserve = ['ref', 'challengeId', 'social', 'comp', 'intent', 'redirect', 'ad_id', 'campaign_id', 'autoJoinChallenge'];
    paramsToPreserve.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        referralParams[param] = value;
        sessionStorage.setItem(`google_oauth_${param}`, value);
      }
    });

    console.log('[GOOGLE AUTH] Stored referral parameters before OAuth:', referralParams);

    const { error } = await signInWithGoogle();

    if (error) {
      setError(error.message);
      toast({
        title: "Google sign in failed",
        description: error.message,
        variant: "destructive",
      });
      setIsLoading(false);
    }
    // Note: On success, user will be redirected to Google OAuth, so no need to set loading to false
  };

  const handleFacebookSignIn = async () => {
    setIsLoading(true);
    setError("");

    // CRITICAL: Preserve referral parameters before OAuth redirect
    // Store all referral-related parameters in sessionStorage
    const urlParams = new URLSearchParams(window.location.search);
    const referralParams: { [key: string]: string } = {};
    
    // Capture all potential referral parameters
    const paramsToPreserve = ['ref', 'challengeId', 'social', 'comp', 'intent', 'redirect', 'ad_id', 'campaign_id', 'autoJoinChallenge'];
    paramsToPreserve.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        referralParams[param] = value;
        sessionStorage.setItem(`facebook_oauth_${param}`, value);
      }
    });

    console.log('[FACEBOOK AUTH] Stored referral parameters before OAuth:', referralParams);

    const { error } = await signInWithFacebook();

    if (error) {
      setError(error.message);
      toast({
        title: "Facebook sign in failed",
        description: error.message,
        variant: "destructive",
      });
      setIsLoading(false);
    }
    // Note: On success, user will be redirected to Facebook OAuth, so no need to set loading to false
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const fullName = (formData.get("fullName") as string)?.trim() || "";
    const phone = formData.get("phone") as string;
    const dateOfBirth = formData.get("dateOfBirth") as string;
    const termsAccepted = formData.get("termsAccepted") === "on";

    // Split full name into first and last name
    const nameParts = fullName.split(/\s+/).filter(part => part.length > 0);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || firstName; // If no last name, use first name

    // Basic field validation
    if (
      !email ||
      !password ||
      !fullName ||
      !phone ||
      !dateOfBirth
    ) {
      setError("All fields are required");
      setIsLoading(false);
      return;
    }

    // Phone validation
    if (phoneError) {
      setError(phoneError);
      setIsLoading(false);
      return;
    }

    // Check for duplicate phone number
    const phoneAvailable = await checkPhoneAvailability(phone);
    if (phoneAvailable === false) {
      setIsLoading(false);
      return;
    }

    // COMMENTED OUT - ID VERIFICATION REMOVED
    // // Username validation - Removed as per user request
    // // if (!username) {
    // //   setError("Username is required");
    // //   setIsLoading(false);
    // //   return;
    // // }

    // // if (usernameError) {
    // //   setError(usernameError);
    // //   setIsLoading(false);
    // //   return;
    // // }

    // // ID Number validation
    // const idValidation = validateSAIDNumber(idNumber);
    // if (!idValidation.isValid) {
    //   setError(idValidation.error || "Invalid ID number");
    //   setIdNumberError(idValidation.error || "Invalid ID number");
    //   setIsLoading(false);
    //   return;
    // }

    // // Check for duplicate ID number BEFORE creating Supabase account
    // try {
    //   console.log('[ID CHECK] Checking for duplicate ID before signup...');
    //   const idCheckResponse = await fetch('/api/check-id-number', {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify({ idNumber })
    //   });

    //   const idCheckData = await idCheckResponse.json();

    //   if (!idCheckData.available) {
    //     const errorMsg = idCheckData.message || "This ID number is already registered with another account.";
    //     setError(errorMsg);
    //     setIdNumberError(errorMsg);
    //     toast({
    //       title: "ID Already Registered",
    //       description: errorMsg,
    //       variant: "destructive",
    //       duration: 8000,
    //     });
    //     setIsLoading(false);
    //     return;
    //   }

    //   console.log('[ID CHECK] ID number is available, proceeding with signup');
    // } catch (error) {
    //   console.error('[ID CHECK] Error checking ID availability:', error);
    //   setError("Unable to verify ID number. Please try again.");
    //   setIsLoading(false);
    //   return;
    // }

    // // Extract date of birth from ID number
    // // const dateOfBirth = idValidation.dateOfBirth
    // //   ? idValidation.dateOfBirth.toISOString().split('T')[0]
    // //   : null;

    // Terms acceptance verification
    if (!termsAccepted) {
      setError("You must accept the Terms and Conditions to create an account");
      setIsLoading(false);
      return;
    }

    // Age verification
    if (dateOfBirth) {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      if (age < 18) {
        setError("You must be 18 or older to create an account");
        setIsLoading(false);
        return;
      }
    }

    // Include referral code and new fields in signup metadata
    const metadata: any = {
      firstName,
      lastName,
      phone,
      dateOfBirth,
      // idNumber, // COMMENTED OUT - ID verification removed
      // idDocumentType: 'id_number', // COMMENTED OUT - ID verification removed
      termsAccepted,
      ageVerified: true,
      // username, // Removed as username is handled via modal
    };

    // Capture referralCode and challengeId from multiple sources for robustness
    const urlParams = new URLSearchParams(window.location.search);
    const urlReferralCode = urlParams.get("ref");
    const urlChallengeId =
      urlParams.get("challengeId") || urlParams.get("autoJoinChallenge");

    // Use referralCode from state (set on page load) or URL as fallback
    const finalReferralCode = referralCode || urlReferralCode;
    const finalChallengeId =
      urlChallengeId ||
      (window as any).__pendingChallengeId ||
      sessionStorage.getItem("challengeContext_challengeId");

    if (finalReferralCode) {
      metadata.referralCode = finalReferralCode;
      console.log(
        "[AUTH] Including referralCode in signup metadata:",
        finalReferralCode,
      );
    } else {
      console.log("[AUTH] No referralCode found in URL params or state");
    }

    if (finalChallengeId) {
      metadata.challengeId = finalChallengeId;
      metadata.fromChallengePage = true;
      console.log(
        "[AUTH] Including challengeId in signup metadata:",
        finalChallengeId,
      );

      // Store challenge context for post-signup redirect
      sessionStorage.setItem(
        "challengeContext",
        JSON.stringify({
          challengeId: finalChallengeId,
          referralCode: finalReferralCode,
          intent: "join",
        }),
      );

      // Clean up window storage
      if ((window as any).__pendingChallengeId) {
        delete (window as any).__pendingChallengeId;
      }
    } else {
      console.log(
        "[AUTH] No challengeId found in URL params or session storage",
      );
    }

    if (redirect) {
      metadata.redirect = redirect;
    } else if (finalChallengeId) {
      // Default redirect to challenge page if challengeId is present
      metadata.redirect = `/challenges/${finalChallengeId}?intent=join&from=auth`;
    }

    const { error } = await signUp(email, password, metadata);

    if (error) {
      // Check if this is an email verification message
      if (
        error.message.includes("Registration successful!") ||
        error.message ===
          "Please check your email to verify your account before signing in." ||
        error.message.includes("Email not confirmed")
      ) {
        // Store verification message and show on login tab
        setVerificationMessage(
          "Please check your email to verify your account before signing in. The email may take 2-5 minutes to arrive.",
        );
        setActiveTab("signin");
        toast({
          title: "Account created successfully!",
          description:
            "Please check your email (including spam folder) to verify your account.",
          variant: "default",
          duration: 10000,
        });
      } 
      // COMMENTED OUT - ID VERIFICATION REMOVED
      // else if (
      //   error.message.includes("ID number is already registered") ||
      //   error.message.includes("already registered with another account") ||
      //   error.message.includes("ID number can only be used for one account")
      // ) {
      //   // Handle duplicate ID number error
      //   setError(
      //     "This ID number is already registered with another account. Each ID number can only be used for one account.",
      //   );
      //   setIdNumberError(
      //     "This ID number is already registered with another account.",
      //   );
      //   toast({
      //     title: "ID Already Registered",
      //     description:
      //       "This ID number is already registered with another account. Each ID number can only be used for one account.",
      //     variant: "destructive",
      //   });
      // } 
      else if (
        error.message.includes("already registered") ||
        error.message.includes("User already registered") ||
        error.message.includes("already exists") ||
        error.message.includes("duplicate key value") ||
        error.message.includes("already been registered") ||
        (error.message.toLowerCase().includes("email") &&
          error.message.toLowerCase().includes("taken")) ||
        error.code === "23505" || // PostgreSQL unique violation error code
        error.code === "duplicate_email"
      ) {
        // Handle duplicate email error with comprehensive detection
        setError(
          "This email address is already registered. Please use a different email address or try signing in instead.",
        );
        toast({
          title: "Email Already Registered",
          description:
            "This email address is already registered. Please use a different email address or try signing in instead.",
          variant: "destructive",
        });
      } else {
        // Show actual error
        setError(error.message);
        toast({
          title: "Sign up failed",
          description: error.message,
          variant: "destructive",
        });
      }
    } else {
      // User was automatically signed in
      // CRITICAL: Phone verification is MANDATORY - user cannot proceed without it
      const redirectPath =
        redirect ||
        (urlParams.get("comp")
          ? `/competition/${urlParams.get("comp")}`
          : "/user");

      console.log('[SIGNUP] Account created successfully, requiring phone verification before redirect');
      
      toast({
        title: "Account Created!",
        description: "Welcome to Prize Drop! Please verify your phone number to continue.",
        variant: "default",
      });

      // Store phone and show OTP modal (user MUST verify before accessing app)
      setSignupPhone(phone);
      setPendingRedirect(redirectPath);
      setShowOTPModal(true);
    }

    setIsLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;

    const { error } = await resetPassword(email);

    if (error) {
      setError(error.message);
      toast({
        title: "Reset failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setResetEmailSent(true);
      toast({
        title: "Reset email sent!",
        description: "Please check your email for the password reset link.",
        variant: "default",
      });
    }

    setIsLoading(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    const { error } = await updatePassword(password);

    if (error) {
      setError(error.message);
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Password updated!",
        description: "Your password has been updated successfully.",
        variant: "default",
      });
      // Clear URL params and reset state
      window.history.replaceState({}, document.title, "/auth");
      setShowUpdatePassword(false);
    }

    setIsLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Prize Drop</CardTitle>
          <CardDescription>
            Sign in to your account or create a new one
          </CardDescription>
        </CardHeader>
        <CardContent>
          {verificationMessage && (
            <Alert className="mb-4">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{verificationMessage}</AlertDescription>
            </Alert>
          )}

          {referralCode && redirect && (
            <Alert className="mb-4">
              <Gift className="h-4 w-4" />
              <AlertDescription>
                {redirect.includes("/competition/")
                  ? "Welcome! Sign up now and both you and your friend will receive 35 bonus entries for the competition!"
                  : "Welcome! You're signing up from a referral link. Join and stand a chance to win the top prize!"}
              </AlertDescription>
            </Alert>
          )}

          {showUpdatePassword ? (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Reset Your Password
              </h3>
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter new password"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    required
                    disabled={isLoading}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Updating..." : "Update Password"}
                </Button>
              </form>
            </div>
          ) : showResetPassword ? (
            <div>
              <h3 className="text-lg font-semibold mb-4">Reset Password</h3>
              {resetEmailSent ? (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    Password reset email sent! Please check your inbox and
                    follow the instructions.
                  </AlertDescription>
                </Alert>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Sending..." : "Send Reset Email"}
                  </Button>
                </form>
              )}
              <Button
                variant="link"
                className="w-full mt-4"
                onClick={() => {
                  setShowResetPassword(false);
                  setResetEmailSent(false);
                  setError("");
                }}
              >
                Back to Sign In
              </Button>
            </div>
          ) : (
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="space-y-4">
                {/* Email/Password Form - Now at the top */}
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                      data-testid="input-signin-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="signin-password"
                        name="password"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        required
                        disabled={isLoading}
                        data-testid="input-signin-password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-2 flex items-center text-muted-foreground"
                        onClick={handleLoginToggle}
                        aria-label={showLoginPassword ? "Hide password" : "Show password"}
                      >
                        {showLoginPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading}
                    data-testid="button-signin"
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>

                {/* Social Login Separator */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      OR Continue with Socials
                    </span>
                  </div>
                </div>

                {/* Social Login Buttons - Side by Side */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Google Sign In */}
                  <Button
                    onClick={handleGoogleSignIn}
                    variant="outline"
                    className="w-full"
                    disabled={isLoading}
                    data-testid="button-google-signin"
                    title="Continue with Google - Referral tracking enabled"
                  >
                    <FcGoogle className="mr-2 h-4 w-4" />
                    Google
                  </Button>

                  {/* Facebook Sign In */}
                  <Button
                    onClick={handleFacebookSignIn}
                    variant="outline"
                    className="w-full"
                    disabled={isLoading}
                    data-testid="button-facebook-signin"
                    title="Continue with Facebook - Referral tracking enabled"
                  >
                    <FaFacebook className="mr-2 h-4 w-4 text-blue-600" />
                    Facebook
                  </Button>
                </div>

                {/* Magic Link Section - Hidden for now */}
                {false && (
                  <>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <Separator className="w-full" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          Or
                        </span>
                      </div>
                    </div>

                    {magicLinkSent ? (
                      <Alert>
                        <CheckCircle2 className="h-4 w-4" />
                        <AlertDescription>
                          Magic link sent! Please check your inbox and click the
                          link to sign in.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <form
                        onSubmit={handleMagicLinkSignIn}
                        className="space-y-4"
                      >
                        <div className="space-y-2">
                          <Label
                            htmlFor="magic-email"
                            className="flex items-center gap-2"
                          >
                            <Mail className="h-4 w-4" />
                            Sign in with Magic Link
                          </Label>
                          <Input
                            id="magic-email"
                            name="magic-email"
                            type="email"
                            placeholder="Enter your email for magic link"
                            required
                            disabled={isMagicLinkLoading}
                            data-testid="input-magic-link-email"
                          />
                        </div>
                        <Button
                          type="submit"
                          variant="outline"
                          className="w-full"
                          disabled={isMagicLinkLoading}
                          data-testid="button-magic-link"
                        >
                          {isMagicLinkLoading
                            ? "Sending..."
                            : "Send Magic Link"}
                        </Button>
                      </form>
                    )}
                  </>
                )}

                <Link
                  href="/forgot-password"
                  data-testid="link-forgot-password"
                >
                  <Button variant="link" className="w-full text-sm">
                    Forgot your password?
                  </Button>
                </Link>
              </TabsContent>

              <TabsContent value="signup" className="space-y-4">
                {error && (
                  <Alert className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-fullName">Full Name</Label>
                    <Input
                      id="signup-fullName"
                      name="fullName"
                      placeholder="John Doe"
                      required
                      disabled={isLoading}
                      data-testid="input-fullName"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                      onChange={(e) => validateEmail(e.target.value)}
                      className={
                        emailError ? "border-red-300 focus:border-red-500" : ""
                      }
                    />
                    {emailError && (
                      <p className="text-xs text-red-600">{emailError}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        name="password"
                        type={showSignupPassword ? "text" : "password"}
                        placeholder="Example: MyPass123!"
                        required
                        disabled={isLoading}
                        onChange={(e) => validatePassword(e.target.value)}
                        className={`pr-10 ${passwordError ? "border-red-300 focus:border-red-500" : ""}`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-2 flex items-center text-muted-foreground"
                        onClick={handleSignupToggle}
                        aria-label={showSignupPassword ? "Hide password" : "Show password"}
                      >
                        {showSignupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordError && (
                      <p className="text-xs text-red-600">{passwordError}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      At least 8 characters with uppercase, lowercase, and
                      numbers
                    </p>
                  </div>

                  {/* Phone Number */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-phone">Phone Number</Label>
                    <Input
                      id="signup-phone"
                      name="phone"
                      type="tel"
                      placeholder="27712345678 or 0712345678"
                      required
                      disabled={isLoading}
                      onChange={(e) => {
                        // Sanitize: only allow digits, +, spaces, and dashes
                        const sanitized = e.target.value.replace(/[^\d+\s\-]/g, '');
                        e.target.value = sanitized;
                        validatePhone(sanitized);
                      }}
                      onBlur={(e) => checkPhoneAvailability(e.target.value)}
                      className={
                        phoneError ? "border-red-300 focus:border-red-500" : ""
                      }
                      data-testid="input-phone"
                    />
                    {phoneError && (
                      <p className="text-xs text-red-600">{phoneError}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      South African phone number
                    </p>
                  </div>

                  {/* Date of Birth */}
                  <div className="space-y-2">
                    <Label htmlFor="signup-dateOfBirth">Date of Birth</Label>
                    <Input
                      id="signup-dateOfBirth"
                      name="dateOfBirth"
                      type="date"
                      required
                      disabled={isLoading}
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      data-testid="input-dateOfBirth"
                    />
                    <p className="text-xs text-muted-foreground">
                      You must be 18 or older to use Prize Drop
                    </p>
                  </div>

                  {/* Terms and Conditions */}
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="termsAccepted"
                        name="termsAccepted"
                        required
                        disabled={isLoading}
                      />
                      <div className="grid gap-1.5 leading-none">
                        <Label
                          htmlFor="termsAccepted"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          I accept the{" "}
                          <Link href="/terms">
                            <span className="text-primary hover:underline cursor-pointer">
                              Terms and Conditions
                            </span>
                          </Link>
                          , have read the{" "}
                          <Link href="/privacy">
                            <span className="text-primary hover:underline cursor-pointer">
                              Privacy Policy
                            </span>
                          </Link>{" "}
                          and{" "}
                          <Link href="/disclaimer">
                            <span className="text-primary hover:underline cursor-pointer">
                              Disclaimer
                            </span>
                          </Link>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Required to create an account and participate in
                          competitions
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}
          <div className="mt-4 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-gray-600 hover:text-gray-800"
            >
              Back to Homepage
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* OTP Verification Dialog - MANDATORY, cannot be dismissed */}
      <Dialog open={showOTPModal}>
        <DialogContent 
          className="sm:max-w-md" 
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="mb-4">
            <h2 className="text-xl font-bold text-center mb-2">Phone Verification Required</h2>
            <p className="text-sm text-gray-600 text-center">
              You must verify your phone number before accessing your account.
            </p>
          </div>
          <OTPVerification
            phone={signupPhone}
            onVerified={() => {
              setShowOTPModal(false);
              toast({
                title: "Phone Verified!",
                description: "Your phone number has been verified successfully.",
                variant: "default",
              });
              // Navigate to pending redirect path
              if (pendingRedirect) {
                navigate(pendingRedirect);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
